import { Calendar, CalendarTimeElementClass } from './calendar.component';
import { ClassBuilder } from '../../types/calendar.types';
import { localized, msg } from '@lit/localize';
import { RoundtripOfferResponse, RoundtripOfferViewModel } from '../../types/offer.types';
import { html, type PropertyValues, TemplateResult } from 'lit';
import { classMap } from 'lit-html/directives/class-map.js';
import { property, state } from 'lit/decorators.js';
import { AnalyticsService } from '../../services/analytics.service';

@localized()
export class CalendarDefault extends Calendar {
    protected override createRenderRoot(): HTMLElement | DocumentFragment {
        return this; // Light DOM – render inside parent's Shadow DOM
    }

    @property({ type: String, attribute: 'phone-number' })
    phoneNumber: string = '';

    @property({ type: String, attribute: 'roundtrip-key' })
    roundtripKey: string = '';

    @property({ type: String, attribute: 'roundtrip-id' })
    roundtripId: string = '';

    @property({ type: String, attribute: 'start-date' })
    startDate: string = '';

    @property({ type: Object })
    roundtripOfferResponse: RoundtripOfferResponse = { offers: [], nextOffer: null, bestPrice: null };

    @property({ type: String, attribute: 'selected-month' })
    selectedMonth: string = new Date().toISOString().substring(0, 7);

    @state() private bestOffer?: RoundtripOfferViewModel;
    @state() private hasNextOffer: boolean = false;
    @state() private nextOfferStartDate: string = '';
    @state() private offers: RoundtripOfferViewModel[] = [];
    @state() private selectedDay: string = '';
    @state() private hasBestPriceBullet: boolean = false;

    private _selectedOffer?: RoundtripOfferViewModel;

    constructor() {
        super();
        this.dayRenderer = this.roundtripDayRenderer;
    }

    override connectedCallback(): void {
        super.connectedCallback();
        this.offers = this.roundtripOfferResponse.offers;
        this.bestOffer = this.offers.reduce(
            (bestOffer, curr) => (!bestOffer || bestOffer.price.amount > curr.price.amount ? curr : bestOffer),
            this.offers[0]
        );
        const earliestOffer = this.offers.reduce(
            (earliest, curr) => (!earliest || curr.startDate < earliest.startDate ? curr : earliest),
            this.offers[0]
        );
        this.hasNextOffer = this.roundtripOfferResponse.nextOffer != null;
        this.nextOfferStartDate = this.roundtripOfferResponse.nextOffer?.startDate ?? earliestOffer?.startDate ?? '';
        this.updateHasNextOfferForSelectedMonth();
    }

    protected warn(name: string, data: object): void {
        const detail: { name: string; data: object } = { name: name.toLowerCase(), data };

        this.dispatchEvent(new CustomEvent('der.warning', { detail, bubbles: true, composed: true }));
    }

    protected override updated(changed: PropertyValues): void {
        super.updated(changed);

        if (changed.has('selectedMonth')) {
            const [yearString, monthString] = this.selectedMonth.split('-');
            const parsedYear = Number(yearString);
            const parsedMonth = Number(monthString);
            const isValidMonth =
                Number.isInteger(parsedYear) && Number.isInteger(parsedMonth) && parsedMonth >= 1 && parsedMonth <= 12;

            // If parent controls the month, align visible calendar first to avoid reading stale DOM month back.
            if (isValidMonth && (this.activeYear !== parsedYear || this.activeMonth !== parsedMonth)) {
                this.activeYear = parsedYear;
                this.activeMonth = parsedMonth;
                return;
            }
        }

        const firstTimeElement = this.renderRoot.querySelector('time.c-calendar__day');
        const visibleMonth = firstTimeElement?.getAttribute('datetime')?.substring(0, 7);
        if (visibleMonth && visibleMonth !== this.selectedMonth) {
            this.selectedMonth = visibleMonth;
        }

        const selectedTimeElement = this.renderRoot.querySelector('time.c-calendar__day[aria-selected="true"]');
        if (selectedTimeElement) {
            const selectedDate = selectedTimeElement.getAttribute('datetime');
            if (selectedDate && selectedDate !== this.selectedDay) {
                if (this.hasPriceForIsoDate(selectedDate)) {
                    this.selectedDay = selectedDate;
                }
            }
        }
        if (changed.has('selectedMonth') || changed.has('offers')) {
            this.updateHasNextOfferForSelectedMonth();
        }

        if (changed.has('selectedMonth')) {
            this.dispatchEvent(
                new CustomEvent('der-tm-calendar-month-change', {
                    detail: { month: this.selectedMonth },
                    bubbles: true,
                    composed: true,
                })
            );
        }
    }

    private updateHasNextOfferForSelectedMonth(): void {
        if (!this.selectedMonth) {
            return;
        }

        const hasPricesInCurrentMonth = this.offers.some(
            (offer) => offer.startDate.substring(0, 7) === this.selectedMonth
        );
        this.hasNextOffer = !hasPricesInCurrentMonth && !!this.nextOfferStartDate;
    }

    private hasPriceForIsoDate(dateIso: string): boolean {
        if (!dateIso) return false;
        return this.offers.some((d) => d.startDate.substring(0, 10) === dateIso);
    }

    private formatNextOfferDate(isoDate: string): string {
        if (!isoDate) return '';

        const date = new Date(isoDate);

        const formatter = new Intl.DateTimeFormat(this.lang, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });

        // If not French, just return the default localized format
        if (this.lang !== 'fr-CH') {
            return formatter.format(date);
        }

        // Special French rule for "1er"
        const parts = formatter.formatToParts(date);
        const day = parts.find((p) => p.type === 'day')?.value || '';
        const month = parts.find((p) => p.type === 'month')?.value || '';
        const year = parts.find((p) => p.type === 'year')?.value || '';

        const formattedDay = day === '1' ? '1er' : day;
        return `le ${formattedDay} ${month} ${year}`;
    }

    private get isButtonEnabled(): boolean {
        const hasSelectedDay = !!this.selectedDay;
        const hasPrice = this.selectedDay ? this.hasPriceForIsoDate(this.selectedDay) : false;
        const hasPriceData = this.offers.length > 0;
        const hasNextOffer = this.hasNextOffer;

        return (hasSelectedDay && hasPrice && hasPriceData) || hasNextOffer;
    }

    private navigateToMonth(dateString: string): void {
        if (!dateString) return;

        const date = new Date(dateString);
        const month = date.getMonth() + 1;
        const year = date.getFullYear();

        this.activeMonth = month;
        this.activeYear = year;

        this.selectedMonth = dateString.substring(0, 7);
        this.updateHasNextOfferForSelectedMonth();
        this.requestUpdate();
    }

    private handleBookingClick(): void {
        if (this.hasNextOffer && this.nextOfferStartDate) {
            this.navigateToMonth(this.nextOfferStartDate);
        } else if (this.selectedDay) {
            this.openBookingRequestEvent(true);
        }
    }

    private handleBookingWithoutDateClick(): void {
        this.openBookingRequestEvent(false);
    }

    private findPriceForDate(day: number): RoundtripOfferViewModel | undefined {
        const baseYearMonth =
            this.selectedMonth ||
            (this.startDate
                ? this.startDate.substring(0, 7)
                : this.offers.length > 0
                  ? this.offers[0]?.startDate.substring(0, 7)
                  : new Date().toISOString().substring(0, 7));

        const formattedDate = `${baseYearMonth}-${String(day).padStart(2, '0')}`; // YYYY-MM-DD

        return this.offers.find((data) => data.startDate.substring(0, 10) === formattedDate);
    }

    private openBookingRequestEvent(hasDate: boolean): void {
        // Dispatch analytics event first
        this._dispatchAnalyticsEvent(hasDate);
        // Dispatch a custom event that parent components can listen to
        if (hasDate) {
            this.dispatchEvent(
                new CustomEvent('der-tm-roundtrip-calendar-click-action', {
                    detail: this._selectedOffer,
                    bubbles: true,
                    composed: true,
                })
            );
        } else {
            this.dispatchEvent(
                new CustomEvent('der-tm-roundtrip-calendar-click-action', {
                    detail: 'noDateSelected',
                    bubbles: true,
                    composed: true,
                })
            );
        }
    }

    private _dispatchAnalyticsEvent(hasDate: boolean): void {
        const eventData = AnalyticsService.buildOfferPanelEvent(
            hasDate ? 'cta_request_withdate' : 'cta_request_nodate',
            this.id ?? '',
            hasDate ? 'request for specific date' : 'request without a date',
            'roundtrip offer request',
            this.lang
        );
        AnalyticsService.dispatch(this, eventData);
    }

    private handleRoundtripDayClick(offer: RoundtripOfferViewModel | undefined): void {
        if (!offer) return;
        this._selectedOffer = offer;
        this.selectedDay = offer.startDate.slice(0, 10);
        this.requestUpdate();
    }

    private handleRoundtripDayKeydown(event: KeyboardEvent, offer: RoundtripOfferViewModel | undefined): void {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.handleRoundtripDayClick(offer);
        }
    }

    private getRequestOfferSubtitle(): string {
        const currency = this.bestOffer?.price?.currency?.trim();
        return currency ? `${msg('requestOfferButtonSubLabel')} ${currency}` : '';
    }

    override getCustomTimeElementClassBuilders(day: number | null, month: number, year: number): ClassBuilder[] {
        const offer = day ? this.findPriceForDate(day) : undefined;
        const dateIsoString = `${this.selectedMonth}-${String(day).padStart(2, '0')}`;
        const hasPrice = day && offer ? this.hasPriceForIsoDate(dateIsoString) : false;
        const isUnselectableDueToNoPrice = !!day && !hasPrice;

        return [
            ...super.getCustomTimeElementClassBuilders(day, month, year),
            { class: [CalendarTimeElementClass.Unselectable], case: isUnselectableDueToNoPrice },
        ];
    }

    roundtripDayRenderer(day: number | null, isUnselectable: boolean): TemplateResult {
        const isValidForEarliestBookingDate =
            day && this.isValidForEarliestBookingDate(day, this.activeMonth, this.activeYear);
        const dateIsoString = `${this.selectedMonth}-${String(day).padStart(2, '0')}`;
        const offer = day && isValidForEarliestBookingDate ? this.findPriceForDate(day) : undefined;
        const hasPrice = day && offer ? this.hasPriceForIsoDate(dateIsoString) : false;
        const isUnselectableDueToNoPrice = day && !hasPrice;
        const isSelected = day && this.selectedDay === dateIsoString;

        return html` <span
            class=${classMap({
                'm-calendar-default__day': true,
                'm-calendar-default__day--selected': !!isSelected,
                'm-calendar-default__day--best-date': (offer?.price.bestPrice && this.hasBestPriceBullet) ?? false,
            })}
            tabindex="${isUnselectable || isUnselectableDueToNoPrice ? '-1' : 0}"
            style="${isUnselectableDueToNoPrice ? 'pointer-events: none;' : ''}"
            @click="${day && hasPrice ? () => this.handleRoundtripDayClick(offer) : undefined}"
            @keydown="${day ? (event: KeyboardEvent) => this.handleRoundtripDayKeydown(event, offer) : undefined}"
            role="button"
            aria-label="${day ? `Select ${day} ${this.selectedMonth}` : ''}"
        >
            <span class="m-calendar-default__day-number">${day}</span>
            <span class="m-calendar-default__day-price"> ${offer ? offer.price.formattedString : '-'} </span>
        </span>`;
    }

    override render() {
        return html`<div
            class=${classMap({
                'm-calendar-default': true,
                'm-calendar-default--disabled': this.hasNextOffer,
            })}
        >
            <p class="m-calendar-default__header-text">${msg(html`calendarTitle`)}</p>
            ${super.render()}
            <div class="m-calendar-default__button-text-wrapper">
                <der-ui-button
                    variant=${this.hasNextOffer ? 'info' : 'primary'}
                    .disabled=${!this.isButtonEnabled}
                    @click=${this.handleBookingClick}
                    .wrapText=${true}
                    subtitle=${this.hasNextOffer
                        ? this.formatNextOfferDate(this.nextOfferStartDate)
                        : this.getRequestOfferSubtitle()}
                >
                    ${this.hasNextOffer ? msg('nextOfferDateButtonLabel') : msg('requestOfferButtonLabel')}
                </der-ui-button>
                <der-ui-button variant="secondary" @click=${this.handleBookingWithoutDateClick}
                    >${msg('nextWithoutDate')}</der-ui-button
                >
                ${!this.phoneNumber
                    ? ''
                    : html` <div class="m-calendar-default__contact">
                          <der-ui-icon-label icon="systemPhone">
                              <span slot="value">
                                  ${msg(
                                      html`calendarInfoText <a href="tel:${this.phoneNumber}">${this.phoneNumber}</a>`
                                  )}
                              </span>
                          </der-ui-icon-label>
                      </div>`}
            </div>
        </div>`;
    }
}
